/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";
import { useEffect, useState } from "react";

function Dashboard() {
  const dashboardCardHeight = "280px";
  const dashboardCardSx = {
    height: dashboardCardHeight,
    display: "flex",
    flexDirection: "column",
    border: "1px solid",
    borderColor: "rgba(76, 175, 80, 0.2)",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 14px 30px rgba(15, 23, 42, 0.12)",
    },
  };
  const [refreshTokenCount, setRefreshTokenCount] = useState(0);
  const [errorLogFileCount, setErrorLogFileCount] = useState(0);
  const [databaseSizeMb, setDatabaseSizeMb] = useState(0);
  const [userRoleSummary, setUserRoleSummary] = useState({
    totalUsers: 0,
    membersWithoutRoles: 0,
    roleWiseCounts: [],
  });

  useEffect(() => {
    const fetchRefreshTokensSummary = async () => {
      const endpoint = appSettings?.endpoints?.getAllRefreshTokens;
      if (!endpoint) return;

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      try {
        const response = await fetchWithAuth(url, {
          method: "GET",
          cache: "no-store",
        });

        if (!response.ok) return;

        const data = await response.json();
        const list = Array.isArray(data) ? data : data?.$values;
        setRefreshTokenCount(Array.isArray(list) ? list.length : 0);
      } catch (e) {
        setRefreshTokenCount(0);
      }
    };

    const fetchErrorLogFilesSummary = async () => {
      const endpoint = appSettings?.endpoints?.getErrorLogFiles;
      if (!endpoint) return;

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      try {
        const response = await fetchWithAuth(url, {
          method: "GET",
          cache: "no-store",
        });

        if (!response.ok) return;

        const data = await response.json();
        const list = Array.isArray(data) ? data : data?.$values;
        setErrorLogFileCount(Array.isArray(list) ? list.length : 0);
      } catch (e) {
        setErrorLogFileCount(0);
      }
    };

    const fetchDatabaseSizeSummary = async () => {
      const endpoint = appSettings?.endpoints?.getDatabaseSizeMb;
      if (!endpoint) return;

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      try {
        const response = await fetchWithAuth(url, {
          method: "GET",
          cache: "no-store",
        });

        if (!response.ok) return;

        const data = await response.json();
        const parsedValue = Number(data?.sizeInMb);
        setDatabaseSizeMb(Number.isFinite(parsedValue) ? parsedValue : 0);
      } catch (e) {
        setDatabaseSizeMb(0);
      }
    };

    const fetchUserRoleCountSummary = async () => {
      const endpoint = appSettings?.endpoints?.getUserRoleCountSummary;
      if (!endpoint) return;

      const url = `${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`;
      try {
        const response = await fetchWithAuth(url, {
          method: "GET",
          cache: "no-store",
        });

        if (!response.ok) return;

        const data = await response.json();
        const totalUsers = Number(data?.totalUsers);
        const membersWithoutRoles = Number(data?.membersWithoutRoles);
        const rawRoleCounts = Array.isArray(data?.roleWiseCounts)
          ? data.roleWiseCounts
          : data?.roleWiseCounts?.$values;

        setUserRoleSummary({
          totalUsers: Number.isFinite(totalUsers) ? totalUsers : 0,
          membersWithoutRoles: Number.isFinite(membersWithoutRoles) ? membersWithoutRoles : 0,
          roleWiseCounts: Array.isArray(rawRoleCounts) ? rawRoleCounts : [],
        });
      } catch (e) {
        setUserRoleSummary({ totalUsers: 0, membersWithoutRoles: 0, roleWiseCounts: [] });
      }
    };

    fetchRefreshTokensSummary();
    fetchErrorLogFilesSummary();
    fetchDatabaseSizeSummary();
    fetchUserRoleCountSummary();
  }, []);

  const defaultGaugeOptions = {
    gaugeMax: 200,
    dialMarks: [0, 50, 100, 150, 200],
    dialRanges: [
      { from: 0, to: 50, color: "#ffffff" },
      { from: 50, to: 100, color: "#fdd835" },
      { from: 100, to: 150, color: "#fb8c00" },
      { from: 150, to: 200, color: "#e53935" },
    ],
    formatValue: (value) => value,
  };

  const dbSizeGaugeOptions = {
    gaugeMax: 500,
    dialMarks: [0, 100, 200, 300, 500],
    dialRanges: [
      { from: 0, to: 100, color: "#43a047" },
      { from: 100, to: 200, color: "#fdd835" },
      { from: 200, to: 300, color: "#fb8c00" },
      { from: 300, to: 500, color: "#e53935" },
    ],
    formatValue: (value) => `${Math.round(value * 100) / 100} MB`,
  };

  const polarToCartesian = (cx, cy, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180;
    return {
      x: cx + radius * Math.cos(angleInRadians),
      y: cy + radius * Math.sin(angleInRadians),
    };
  };

  const renderGaugeCard = (title, value, icon, iconColor, options = defaultGaugeOptions) => {
    const gaugeStartAngle = 180;
    const gaugeEndAngle = 360;
    const gaugeMax = options.gaugeMax ?? defaultGaugeOptions.gaugeMax;
    const dialMarks = options.dialMarks ?? defaultGaugeOptions.dialMarks;
    const dialRanges = options.dialRanges ?? defaultGaugeOptions.dialRanges;
    const formatValue = options.formatValue ?? defaultGaugeOptions.formatValue;

    const valueToAngle = (pointValue) =>
      gaugeStartAngle + (pointValue / gaugeMax) * (gaugeEndAngle - gaugeStartAngle);

    const getNeedleAngle = (pointValue) => {
      const clamped = Math.min(Math.max(pointValue, 0), gaugeMax);
      return gaugeStartAngle + (clamped / gaugeMax) * (gaugeEndAngle - gaugeStartAngle);
    };

    const createArcPath = (cx, cy, radius, startValue, endValue) => {
      const startAngle = valueToAngle(startValue);
      const endAngle = valueToAngle(endValue);
      const start = polarToCartesian(cx, cy, radius, startAngle);
      const end = polarToCartesian(cx, cy, radius, endAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
      return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
    };

    const needleAngle = getNeedleAngle(value);

    return (
      <Card sx={dashboardCardSx}>
        <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
          <MDBox
            variant="gradient"
            bgColor={iconColor}
            color="white"
            coloredShadow={iconColor}
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <Icon fontSize="medium" color="inherit">
              {icon}
            </Icon>
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography
              variant="caption"
              fontWeight="regular"
              color="text"
              sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
            >
              {title}
            </MDTypography>
            <MDTypography variant="h4" color="text">
              {formatValue(value)}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox p={2} pt={1} sx={{ flex: 1 }}>
          <MDBox mt={2} mx="auto" width="100%" maxWidth="220px">
            <svg viewBox="0 0 220 150" width="100%" height="auto">
              {dialRanges.map((range) => (
                <path
                  key={`${title}-range-${range.from}-${range.to}`}
                  d={createArcPath(110, 105, 70, range.from, range.to)}
                  stroke={range.color}
                  strokeWidth="16"
                  fill="none"
                  strokeLinecap="butt"
                />
              ))}

              {Array.from({ length: 21 }).map((_, index) => {
                const tickValue = (gaugeMax / 20) * index;
                const angle = valueToAngle(tickValue);
                const outer = polarToCartesian(110, 105, 84, angle);
                const inner = polarToCartesian(110, 105, 76, angle);
                return (
                  <line
                    key={`${title}-tick-${index}`}
                    x1={outer.x}
                    y1={outer.y}
                    x2={inner.x}
                    y2={inner.y}
                    stroke="#9e9e9e"
                    strokeWidth="1.5"
                  />
                );
              })}

              {dialMarks.map((mark) => {
                const angle = valueToAngle(mark);
                const position = polarToCartesian(110, 105, 98, angle);
                return (
                  <text
                    key={`${title}-label-${mark}`}
                    x={position.x}
                    y={position.y}
                    fill="#757575"
                    fontSize="13"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {mark}
                  </text>
                );
              })}

              <line
                x1="110"
                y1="105"
                x2={polarToCartesian(110, 105, 58, needleAngle).x}
                y2={polarToCartesian(110, 105, 58, needleAngle).y}
                stroke="#757575"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <circle cx="110" cy="105" r="5" fill="#757575" />
            </svg>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  const renderUserRoleSummaryCard = () => (
    <Card sx={dashboardCardSx}>
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor="success"
          color="white"
          coloredShadow="success"
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">
            groups
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography
            variant="caption"
            fontWeight="regular"
            color="text"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            Total Users
          </MDTypography>
          <MDTypography variant="h4" color="text">
            {userRoleSummary.totalUsers}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox p={2} pt={1} sx={{ flex: 1, minHeight: 0 }}>
        <MDBox display="flex" justifyContent="space-between" py={0.5}>
          <MDTypography variant="caption" color="text">
            Members Without Roles
          </MDTypography>
          <MDTypography variant="caption" fontWeight="medium" color="text">
            {userRoleSummary.membersWithoutRoles}
          </MDTypography>
        </MDBox>
        <MDTypography variant="button" fontWeight="light" color="text">
          Role Wise User Counts
        </MDTypography>
        <MDBox
          mt={1}
          sx={{
            maxHeight: "140px",
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          {userRoleSummary.roleWiseCounts.length > 0 ? (
            userRoleSummary.roleWiseCounts.map((item) => (
              <MDBox
                key={`role-count-${item.roleId ?? item.RoleId ?? item.roleName ?? item.RoleName}`}
                display="flex"
                justifyContent="space-between"
                py={0.5}
              >
                <MDTypography variant="caption" color="text">
                  {item.roleName ?? item.RoleName ?? "-"}
                </MDTypography>
                <MDTypography variant="caption" fontWeight="medium" color="text">
                  {item.userCount ?? item.UserCount ?? 0}
                </MDTypography>
              </MDBox>
            ))
          ) : (
            <MDTypography variant="caption" color="text">
              No roles available.
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {renderGaugeCard("Refresh Tokens", refreshTokenCount, "key", "warning")}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {renderGaugeCard("Error Log Files", errorLogFileCount, "error", "error")}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {renderGaugeCard(
                "Database Size",
                databaseSizeMb,
                "storage",
                "success",
                dbSizeGaugeOptions
              )}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>{renderUserRoleSummaryCard()}</MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
